import type {State} from 'src/reducers';
import type {
  AtsEntity,
  AtsEntities,
  AtsEntityMappings,
  EntityAttributeSchema,
  RelatedEntityAttributeSchema,
  EntityMappingsByName,
  EntityMappingsBySenseName,
  EntityType,
  EntitySenseName,
  EntityRelationship,
  AtsClientEntity,
  EntityMetadataObject,
} from 'src/types/ats-entities';
import type {EntityFilter} from 'src/types/ats-settings';
import type {DynamicLabels} from 'src/action-creators/dynamic-labels';
import type {RecipientResource} from 'src/types/survey';

import {createSelector} from 'reselect';
import get from 'lodash/get';
import keyBy from 'lodash/keyBy';

import logger from 'src/utils/logger';
import {captureSentryMessage} from 'src/utils/sentry';

import {peopleEntityTypes} from 'src/types/ats-entities';
import isEmpty from 'lodash/isEmpty';

/*
 * Base Selectors
 */

export const workflowEntityType = (
  state: State,
  workflowId: string,
): EntityType | null | undefined =>
  state.workflows.workflows[workflowId]?.entityType;

export const eventEntityType: (arg1: State) => EntityType = (state) =>
  state.eventCreation.entityType;

export const eventRecipientResource = (state: State): RecipientResource =>
  state.eventCreation.recipientResource;

export const getEntityAttributeSchema: (
  arg1: State,
) => Map<string, EntityAttributeSchema> = (state) =>
  state.atsEntities.simpleAttributes;

export const getUnsafeEntityAttributeSchema: (
  arg1: State,
) => Map<string, EntityAttributeSchema> = (state) =>
  state.atsEntities.allAttributes;

export const selectEntityDirectAttributesByType: (
  state: State,
  entityType: EntityType,
) => EntityAttributeSchema[] = createSelector(
  getEntityAttributeSchema,
  // @ts-expect-error - TS7006 - Parameter '_' implicitly has an 'any' type. | TS7006 - Parameter 'entityType' implicitly has an 'any' type.
  (_, entityType) => entityType,
  (attributes, entityType) =>
    // @ts-expect-error - TS2769 - No overload matches this call.
    Array.from(filterEntityAttributesForEntityType(attributes, entityType)),
);

export const selectUnsafeEntityDirectAttributesByType: (
  state: State,
  entityType: EntityType,
) => EntityAttributeSchema[] = createSelector(
  getUnsafeEntityAttributeSchema,
  // @ts-expect-error - TS7006 - Parameter '_' implicitly has an 'any' type. | TS7006 - Parameter 'entityType' implicitly has an 'any' type.
  (_, entityType) => entityType,
  (attributes, entityType) =>
    // @ts-expect-error - TS2769 - No overload matches this call.
    Array.from(filterEntityAttributesForEntityType(attributes, entityType)),
);

// @ts-expect-error - TS2322 - Type 'OutputParametricSelector<any, EntityType, Record<string, any>, (res: EntityAttributeSchema[]) => Record<string, any>>' is not assignable to type '(state: any, entityType: EntityType) => EntityAttributeSchema[]'.
export const selectEntityDirectAttributesForTypeByDisplayName: (
  state: State,
  entityType: EntityType,
) => EntityAttributeSchema[] = createSelector(
  selectEntityDirectAttributesByType,
  (attributes) => {
    const attributesDict: Record<string, any> = {};
    attributes.forEach((attribute) => {
      attributesDict[attribute.display_name] = attribute;
    });
    return attributesDict;
  },
);

export const selectEntityMetadata = (
  state: State,
):
  | {
      [key: string]: EntityMetadataObject;
    }
  | null
  | undefined => state.atsEntities.entityMetadata;

export const selectSpecificEntityMetadata: (
  arg1: State,
  arg2: string,
) => EntityMetadataObject | null | undefined = createSelector(
  selectEntityMetadata,
  // @ts-expect-error - TS7006 - Parameter '_' implicitly has an 'any' type. | TS7006 - Parameter 'entityType' implicitly has an 'any' type.
  (_, entityType) => entityType,
  (entityMetadata, entityType) => entityMetadata?.[entityType],
);

export const getAtsEntityMappings = (state: State): AtsClientEntity[] =>
  state.atsEntities.entities;

export const atsEntitiesAsSelectValues: (arg1: State) => DynamicLabels =
  createSelector(getAtsEntityMappings, (mappings) =>
    mappings.map((mapping) => ({
      label: mapping.display_name_capitalized ?? mapping.display_name,
      value: mapping.name,
    })),
  );

export const getEntityTypeMappings = (state: State): AtsEntity[] => [
  // @ts-expect-error - TS2339 - Property 'entities' does not exist on type 'never'.
  ...state.atsEntities.entities,
  {
    // @ts-expect-error - TS2339 - Property 'entities' does not exist on type 'never'.
    name: 'global_variable',
    display_name: 'global variable',
    display_name_plural: 'global variables',
    // @ts-expect-error - TS2339 - Property 'entities' does not exist on type 'never'.
    sense_name: 'global_variable',
    url: null,
    is_person: false,
    is_candidate_tab: false,
    candidate_tab_order: -1,
    event_entity: false,
    related_entities: [],
    is_internal: false,
    is_csv_entity: false,
  },
  //(diwakersurya) for job match variables
  {
    // @ts-expect-error - TS2339 - Property 'entities' does not exist on type 'never'.
    name: 'job_match_variable',
    display_name: 'job match variable',
    display_name_plural: 'job match variables',
    // @ts-expect-error - TS2339 - Property 'entities' does not exist on type 'never'.
    sense_name: 'job_match_variable',
    url: null,
    is_person: false,
    is_candidate_tab: false,
    candidate_tab_order: -1,
    event_entity: false,
    related_entities: [],
    is_internal: false,
    is_csv_entity: false,
  },
];

export const selectEntityTypeMappingsByName: (
  arg1: State,
) => EntityMappingsByName = createSelector(getEntityTypeMappings, (mappings) =>
  keyBy(mappings, 'name'),
);

export const selectEntityMappingsByName: (arg1: State) => EntityMappingsByName =
  createSelector(getAtsEntityMappings, (mappings) => keyBy(mappings, 'name'));

export const selectEntityMappingsBySenseName: (
  arg1: State,
) => EntityMappingsBySenseName = createSelector(
  getAtsEntityMappings,
  (mappings) => keyBy(mappings, 'sense_name'),
);

export const selectEntityTypeBySenseName = (
  state: State,
  senseName: EntitySenseName,
): EntityType | null | undefined =>
  selectEntityMappingsBySenseName(state)[senseName]?.name;

export const selectEntityByName = (state: State, name: EntityType): AtsEntity =>
  // @ts-expect-error - TS2322 - Type 'AtsEntity | undefined' is not assignable to type 'AtsEntity'.
  selectEntityMappingsByName(state)[name];

export const selectEntityBySenseName = (
  state: State,
  senseName: EntitySenseName,
  // @ts-expect-error - TS2322 - Type 'AtsEntity | undefined' is not assignable to type 'AtsEntity'.
): AtsEntity => selectEntityMappingsBySenseName(state)[senseName];

export const selectEntitiesKeyedByUrl: (arg1: State) => AtsEntityMappings =
  createSelector(getAtsEntityMappings, (mappings) => keyBy(mappings, 'url'));

export const selectEntityByUrl: (arg1: State, arg2: string) => AtsEntity =
  createSelector(
    selectEntitiesKeyedByUrl,
    // @ts-expect-error - TS7006 - Parameter '_' implicitly has an 'any' type. | TS7006 - Parameter 'url' implicitly has an 'any' type.
    (_, url) => url,
    (entities, url) => entities[url],
  );

// @ts-expect-error - TS2322 - Type 'OutputSelector<any, (AtsEntity | undefined)[], (res: Partial<Record<EntityType, AtsEntity>>) => (AtsEntity | undefined)[]>' is not assignable to type '(arg1: any) => AtsEntities'.
export const selectPeopleEntities: (arg1: State) => AtsEntities =
  createSelector(selectEntityMappingsByName, (mappings) =>
    peopleEntityTypes.map((type) => mappings[type]).filter(Boolean),
  );

export const selectPeopleEntitiesKeyedByUrl: (
  arg1: State,
) => AtsEntityMappings = createSelector(selectPeopleEntities, (entities) =>
  keyBy(entities, 'url'),
);

export const selectPeopleEntityByUrl: (arg1: State, arg2: string) => AtsEntity =
  createSelector(
    selectPeopleEntitiesKeyedByUrl,
    // @ts-expect-error - TS7006 - Parameter '_' implicitly has an 'any' type. | TS7006 - Parameter 'url' implicitly has an 'any' type.
    (_, url) => url,
    (entities, url) => entities[url],
  );

export const selectCandidateEntityType = (
  state: State,
): EntityType | null | undefined =>
  selectEntityTypeBySenseName(state, 'candidate');

export const getRelatedEntityType = (
  entities: {
    [key: string]: AtsClientEntity;
  },
  entityType: EntityType | null | undefined,
  attributeName: string,
  // @ts-expect-error - TS7030 - Not all code paths return a value.
): EntityType | null | undefined => {
  const entity = entityType && entities[entityType];
  if (entity) {
    return entity.related_entities.find(
      (relatedEntity) => relatedEntity.name === attributeName,
    )?.entity_type;
  }
};

export const getEntityRelationsForEntityType = (
  entities: AtsClientEntity[],
  entityType: string,
): EntityRelationship[] => {
  const currentEntity: AtsClientEntity | null | undefined = entities.find(
    (entity) => entity.name === entityType,
  );

  if (currentEntity) {
    return currentEntity.related_entities.filter((rel) => {
      const relatedEntity = entities.find((e) => e.name === rel.entity_type);
      if (relatedEntity) {
        return relatedEntity.is_internal;
      }
      return false;
    });
  } else {
    return [];
  }
};

// selects related internal entities for current editing event
// so does not require a prop to be passed in assuming current
// edit event matches the requested entity type.
// if not, see: selectRelatedInternalEntitiesForEntityType(state, entityType)
// @ts-expect-error - TS2322 - Type 'OutputSelector<any, AtsEntity[], (res1: undefined, res2: EntityRelationship[]) => AtsEntity[]>' is not assignable to type '(arg1: any) => EntityRelationship[]'.
export const selectRelatedInternalEntities: (
  arg1: State,
) => EntityRelationship[] = createSelector<
  State,
  undefined,
  EntityRelationship[],
  AtsClientEntity[],
  string
  // @ts-expect-error - TS2345 - Argument of type '(state: State) => AtsClientEntity[]' is not assignable to parameter of type 'ParametricSelector<any, undefined, EntityRelationship[]>'.
>(getAtsEntityMappings, eventEntityType, getEntityRelationsForEntityType);

// select related internal entities for any given entity type
// @ts-expect-error - TS2322 - Type 'OutputSelector<any, AtsEntity[], (res1: string, res2: EntityRelationship[]) => AtsEntity[]>' is not assignable to type '(arg1: any, arg2: string) => EntityRelationship[]'.
export const selectRelatedInternalEntitiesForEntityType: (
  arg1: State,
  arg2: string,
) => EntityRelationship[] = createSelector<
  State,
  string,
  EntityRelationship[],
  AtsClientEntity[],
  string
>(
  // @ts-expect-error - TS2345 - Argument of type '(state: State) => AtsClientEntity[]' is not assignable to parameter of type 'ParametricSelector<any, string, EntityRelationship[]>'.
  getAtsEntityMappings,
  // @ts-expect-error - TS7006 - Parameter '_' implicitly has an 'any' type. | TS7006 - Parameter 'entityType' implicitly has an 'any' type.
  (_, entityType) => entityType,
  getEntityRelationsForEntityType,
);

// @ts-expect-error - TS2322 - Type 'OutputParametricSelector<any, undefined, AtsEntity[], (res: AtsEntity[]) => AtsEntity[]>' is not assignable to type '(arg1: any) => AtsEntity[]'.
export const selectCandidateTabEntities: (arg1: State) => AtsClientEntity[] =
  createSelector<State, undefined, AtsEntity[], AtsEntity[]>(
    getAtsEntityMappings,
    (mappings) =>
      mappings
        .filter(({is_candidate_tab}) => is_candidate_tab)
        .sort((a, b) =>
          // @ts-expect-error - TS18048 - 'a.candidate_tab_order' is possibly 'undefined'. | TS18048 - 'b.candidate_tab_order' is possibly 'undefined'.
          a.candidate_tab_order < b.candidate_tab_order ? -1 : 1,
        ),
  );

export const resolveAttributePathToEntityType = (
  entities: {
    [key: string]: AtsClientEntity;
  },
  entityType: EntityType | null | undefined,
  attributeName: string,
): EntityType | null | undefined => {
  const attributePath = attributeName.split('/').slice(0, -1);

  for (const attributeName of attributePath) {
    entityType = getRelatedEntityType(entities, entityType, attributeName);
    if (!entityType) {
      return;
    }
  }
  return entityType;
};

export const getCandidateFieldName: (arg1: State) => string = (state) =>
  get(getAtsEntityMappings(state), ['candidate', 'name']);

const selectAllEntityRelationships: (
  arg1: State,
) => Map<number, EntityRelationship> = createSelector(
  getAtsEntityMappings,
  (schemas) =>
    schemas
      .values()
      .flatMap((schema) => schema.related_entities)
      .reduce(
        (map, relationship) => map.set(relationship.id, relationship),
        new Map(),
      ),
);

function filterAndGetEntityFilters(
  attributes: Iterable<EntityAttributeSchema>,
): Iterator<EntityFilter> {
  // @ts-expect-error - TS2322 - Type 'IteratorObject<{ selected: boolean; add_to_filters: boolean; attribute_name: string; category: string | null | undefined; data_type: AttributeDataType; display_name: string; entity_type: EntityType; ... 4 more ...; designations?: string[] | undefined; }, undefined, unknown>' is not assignable to type 'Iterator<EntityFilter, any, any>'.
  return Iterator.from(attributes)
    .filter((entityAttribute) => entityAttribute.add_to_filters)
    .map((attr) => ({
      ...attr,
      selected: false,
    }));
}

export const selectEntityFilters: (arg1: State) => EntityFilter[] =
  createSelector(getEntityAttributeSchema, (entityAttributes) =>
    // @ts-expect-error - TS2769 - No overload matches this call.
    Array.from(filterAndGetEntityFilters(entityAttributes.values())),
  );

export function filterEntityAttributesForEntityType(
  attributes: Map<string, EntityAttributeSchema>,
  entityType: EntityType,
): Iterator<EntityAttributeSchema> {
  return Iterator.from(attributes.values()).filter(
    (attribute) => attribute.entity_type === entityType,
  );
}

export function* getRelatedEntityAttributesForEntityType(
  mappings: EntityMappingsByName,
  relationships: Map<number, EntityRelationship>,
  attributes: Map<string, EntityAttributeSchema>,
  entityType: EntityType,
  depth: number = 0,
): Iterator<RelatedEntityAttributeSchema> {
  const mapping = mappings[entityType];

  if (!mapping) {
    return;
  }

  // @ts-expect-error - TS2339 - Property 'map' does not exist on type 'Iterator<EntityAttributeSchema, any, any>'.
  yield* filterEntityAttributesForEntityType(attributes, entityType).map(
    // @ts-expect-error - TS7006 - Parameter 'attr' implicitly has an 'any' type.
    (attr) => ({
      ...attr,
      relationshipPath: [],
      resolvedRelationshipPath: [],
    }),
  );

  depth++;
  // NOTE (kyle): currently we don't support any relationship depth higher than "3"
  if (depth < 3) {
    for (const relationship of mapping.related_entities) {
      const resolvedRelationshipPath = relationship.bridge_step1_id
        ? resolveBridgeRelationshipPath(relationships, relationship)
        : [relationship];

      yield* getRelatedEntityAttributesForEntityType(
        mappings,
        relationships,
        attributes,
        relationship.entity_type,
        depth,
        // @ts-expect-error - TS2339 - Property 'map' does not exist on type 'Iterator<RelatedEntityAttributeSchema, any, any>'. | TS7006 - Parameter 'attr' implicitly has an 'any' type.
      ).map((attr) => ({
        ...attr,
        relationshipPath: [relationship.name, ...attr.relationshipPath],
        resolvedRelationshipPath: [
          ...resolvedRelationshipPath,
          ...attr.resolvedRelationshipPath,
        ],
      }));
    }
  }
}

function resolveBridgeRelationshipPath(
  allRelationships: Map<number, EntityRelationship>,
  relationship: EntityRelationship,
) {
  return (
    [relationship.bridge_step1_id, relationship.bridge_step2_id]
      .filter(Boolean)
      // @ts-expect-error - TS2345 - Argument of type 'number | null | undefined' is not assignable to parameter of type 'number'.
      .map((bridgeId) => allRelationships.get(bridgeId))
  );
}

export function resolveRelatedAttributeEntityType(
  attributeName: string,
  entityType: EntityType,
  mappings: EntityMappingsByName,
): EntityType | null | undefined {
  let mapping = mappings[entityType];

  for (const pathPart of attributeName.split('/').slice(0, -1)) {
    if (!mapping) {
      return;
    }

    const relationship = mapping.related_entities.find(
      (relationship) => relationship.name === pathPart,
    );

    if (!relationship) {
      // no relationship, either a custom var that doesn't fit related entity mapping
      // or a bad agency config
      return;
    }
    mapping = relationship && mappings[relationship.entity_type];
  }

  // @ts-expect-error - TS18048 - 'mapping' is possibly 'undefined'.
  return mapping.name;
}

export const selectRelatedEntityAttributesForEntityType: (
  arg1: State,
  arg2: EntityType,
) => Array<RelatedEntityAttributeSchema> = createSelector(
  selectEntityMappingsByName,
  selectAllEntityRelationships,
  getEntityAttributeSchema,
  // @ts-expect-error - TS7006 - Parameter '_' implicitly has an 'any' type. | TS7006 - Parameter 'entityType' implicitly has an 'any' type.
  (_, entityType) => entityType,
  // @ts-expect-error - TS2769 - No overload matches this call.
  (...params) => Array.from(getRelatedEntityAttributesForEntityType(...params)),
);

export const selectEntityFiltersForType: (
  arg1: State,
  arg2: EntityType,
) => EntityFilter[] = createSelector(
  selectRelatedEntityAttributesForEntityType,
  // @ts-expect-error - TS2769 - No overload matches this call.
  (attributes) => Array.from(filterAndGetEntityFilters(attributes)),
);

export const selectCsvUploadEntities: (arg1: State) => AtsEntities =
  createSelector(getAtsEntityMappings, (mappings) =>
    mappings.filter(({is_csv_entity}) => is_csv_entity),
  );

export const chatbotAllowedEntityTypes: Set<EntitySenseName> = new Set([
  'candidate',
  'bh_job_submission',
]);
// TODO (kyle): remove this
export function selectIsChatbotWorkflow(_state: State): boolean {
  return true;
}
